<template>
  <div ref="container" class="plv-ia-container plv-ia-qn">
    <!-- 左侧 -->
    <Sidebar
      :lang="lang"
      :list="list"
      :is-editing="!!editingData"
      :is-new="isNew"
      :cur-index="curIndex"
      :send-room-list="sendRoomList"
      @download="handleDownloadTemplate"
      @create="handleCreateClick"
      @upload="handleUploadFile"
      @qn-change="changeCurIndex"
    />

    <!-- 右侧 -->
    <div class="plv-ia-qn-main">
      <Edit
        v-if="editingData"
        :lang="lang"
        :editing-data="editingData"
        :error="editError"
        :use-seminar-chat-api="useSeminarChatApi"
        @save="saveQuestionnaire"
        @cancel="handleCancelClick"
      />
      <no-content v-else-if="!list || list.length <= 0" />
      <Detail
        v-else
        :lang="lang"
        :is-stopping="isStopping"
        :questionnaire="list[curIndex]"
        :push-num="pushNum"
        :is-loading="isLoading"
        :is-sent-result="resultSentList.indexOf(list[curIndex].questionnaireId) >= 0"
        @edit="handleEditClick"
        @delete="toggleDeleteQnModal(true)"
        @publish="beforeHandlePublishClick"
        @result="handleResultClick"
        @stop="handleStopClick"
        @download-statistics="handleDownloadStatistics"
      />
    </div>

    <!-- 上一步操作未保存，点击确认将被丢弃 -->
    <PopupConfirm
      ref="discard-modal"
      :tips="t('plvIA.common.confirmDiscardTips')"
      :ensure-txt="t('plvIA.common.confirm')"
      :cancel-txt="t('plvIA.common.cancel')"
      @ensure="handleEnsureDiscard"
      @cancel="handleCancelDiscard"
    />

    <!-- 当前有问卷正在进行，请先前往终止 -->
    <PopupConfirm
      ref="end-qn-modal"
      :tips="t('plvIA.questionnaire.ongoingTips')"
      :ensure-txt="t('plvIA.questionnaire.toStop')"
      :cancel-txt="t('plvIA.common.cancel')"
      :ensure-btn-cls="'plv-ia-btn-new plv-ia-btn-orange-2 pwd-vclass-btn-orange-2'"
      @ensure="handleProcessingModalEnsure"
      @cancel="toggleEndQnModal(false)"
    />

    <!-- 删除后将无法恢复，确认删除？ -->
    <PopupConfirm
      ref="delete-qn-modal"
      :tips="t('plvIA.questionnaire.confirmDelTips')"
      :ensure-txt="t('plvIA.common.delete')"
      :cancel-txt="t('plvIA.common.cancel')"
      :ensure-btn-cls="'plv-ia-btn-new plv-ia-btn-orange-2 pws-vclass-btn-orange-2'"
      @ensure="deleteQuestionnaire"
      @cancel="toggleDeleteQnModal(false)"
    />

    <!-- 研讨会选择人群弹窗 -->
    <send-room-modal
      v-if="showSendRoomModal"
      :send-room-title="sendRoomTitle"
      :lang="lang"
      :send-room-list="sendRoomList"
      :confirm-text="sendRoomConfirmText"
      @change-send-room-list="$emit('change-send-room-list', $event)"
      @confirm="handleSendRoomModalConfirm"
      @close="showSendRoomModal = false"
    />
  </div>
</template>

<script type="text/babel">
import 'core-js/features/array/fill';
import 'core-js/features/array/includes';
import Locale from '@/mixins/locale';
import { debounce } from '@/assets/utils';
import { cloneJSON } from '@polyv/utils/src/lang';
import commonMixin from '../app-mixin';
import PopupConfirm from '@/components/popup/Confirm';
import Sidebar from './Sidebar';
import NoContent from './NoContent';
import Edit from './Edit';
import Detail from './Detail';
import { STATUS, getQuestionnaireModel } from './helper';
import SendRoomModal from '@/components/vclass/send-room-modal';
import sendRoomMixin from '../../mixins/vclass/send-room';

export default {
  name: 'ILSQuestionnaire',

  components: { Edit, Detail, Sidebar, NoContent, PopupConfirm, SendRoomModal },

  mixins: [Locale, commonMixin, sendRoomMixin],
  props: {
    /** 问卷 SDK 实例 */
    core: {
      type: Object,
      default: null,
    },
    /** 需要显示结果的问卷 id */
    resultId: {
      type: String,
      default: '',
    }
  },

  data() {
    const resultSentList = [];
    if (this.resultId) {
      resultSentList.push(this.resultId);
    }
    return {
      // 问卷列表
      list: [],
      curIndex: 0,
      changeIndex: -1, // 要切换的问卷index

      isLoading: false,
      isStopping: false,
      isNew: false, // 新建
      editingData: null,
      editError: null,
      pushNum: 0, // 进行中的问卷提交人数
      resultSentList, // 已发送问卷结果的问卷id列表

      showSendRoomModal: false
    };
  },

  computed: {
    roomId() {
      return this.core.channelInfo.roomId;
    },
    sentResultStorageKey() {
      return `${this.roomId}_qn_sentResult`;
    },
    // 进行中的问卷index
    ongoingIndex() {
      let ongoingIndex = -1;
      this.list.forEach((data, index) => {
        if (ongoingIndex > -1) return;
        if (data.status === STATUS.SENDED) {
          ongoingIndex = index;
        }
      });
      return ongoingIndex;
    },
    // 问卷 id 列表
    qnIdList() {
      return this.list.map((item) => item.questionnaireId);
    },
  },

  beforeDestroy() {
    this.unbindCoreListeners();
  },

  methods: {
    async afterCoreInited() {
      this.bindCoreListeners();
      const list = localStorage.getItem(this.sentResultStorageKey);
      if (list) {
        this.resultSentList = [...this.resultSentList, ...list.split(',')];
      }

      await this.updateList();

      const resultIndex = this.findListIndex(this.resultId);

      if (resultIndex >= 0) {
        this.changeCurIndex(resultIndex, true);
      } else if (this.list?.length) {
        await this.changeCurIndex(0);
      }
    },

    recordSentList(qnIdList) {
      localStorage.setItem(this.sentResultStorageKey, qnIdList.join(','));
    },

    // 绑定 core 事件监听
    bindCoreListeners() {
      this.core
        .on('start', this.handleStartMsg)
        .on('stop', this.handleStopMsg)
        .on('answer', this.handleAnswerMsg)
        .on('show-result', this.handelShowResultMsg)
        .on('update-questionnaire', this.handleUpdateQuestionnaireMsg);
    },

    // 取消绑定 core 事件监听
    unbindCoreListeners() {
      this.core
        .off('start', this.handleStartMsg)
        .off('stop', this.handleStopMsg)
        .off('answer', this.handleAnswerMsg)
        .off('show-result', this.handelShowResultMsg);
    },

    // 收到聊天室消息：开始问卷
    async handleStartMsg(data) {
      const newQuestionnaireId = data.questionnaireId;

      const list = this.list;

      const cloneContent = cloneJSON(data);

      // 1. 是当前的问卷
      // 2. 不是当前的问卷（1. 在列表中 2. 不在列表）
      const curQuestionnaire = list[this.curIndex];
      if (curQuestionnaire && curQuestionnaire.questionnaireId === newQuestionnaireId) {
        const newQuestionnaire = Object.assign({}, curQuestionnaire, cloneContent, {
          status: STATUS.SENDED
        });
        this.$set(this.list, this.curIndex, newQuestionnaire);
        this.pushNum = await this.getPushNum(newQuestionnaireId);
      } else {
        const existedIndex = this.findListIndex(newQuestionnaireId);
        if (existedIndex > -1) {
          const newQuestionnaire = Object.assign({}, list[existedIndex], cloneContent, {
            status: STATUS.SENDED
          });
          this.$set(this.list, existedIndex, newQuestionnaire);
        } else {
          const newQuestionnaire = Object.assign({}, cloneContent, {
            name: cloneContent.name,
            status: STATUS.SENDED
          });
          this.list.unshift(newQuestionnaire);
          this.curIndex++;
        }
      }
    },

    // 收到聊天室消息：结束问卷
    handleStopMsg(data) {
      const curQuestionnaire = this.list[this.curIndex];
      const stopQuestionnaireId = data.questionnaireId;
      // 1. 不是当前的问卷
      // 2. 是当前的问卷
      if (stopQuestionnaireId !== curQuestionnaire.questionnaireId) {
        const stopIndex = this.findListIndex(stopQuestionnaireId);
        const newData = Object.assign({}, this.list[stopIndex], {
          status: STATUS.FINISHED
        });
        this.$set(this.list, stopIndex, newData);
      } else {
        const newData = Object.assign({}, curQuestionnaire, {
          statistics: data.statistics,
          status: STATUS.FINISHED
        });
        this.$set(this.list, this.curIndex, newData);
      }

      this.isStopping = false;
    },

    // 收到聊天室消息：观众回答
    handleAnswerMsg({ questionnaireId }) {
      this.updatePushNum(questionnaireId);
    },

    // 收到聊天室消息：显示答题结果
    handelShowResultMsg({ questionnaireId }) {
      this.changeCurIndex(this.findListIndex(questionnaireId), true);
      this.resultSentList.push(questionnaireId);
      this.recordSentList(this.resultSentList);
    },

    async handleUpdateQuestionnaireMsg(data) {
      await this.updateList();
      if (data.event === 'DELETE_QUESTIONNAIRE') {
        await this.changeCurIndex(0);
      }
    },

    // 取消编辑
    handleCancelClick() {
      this.isNew = false;
      this.editingData = null;
    },

    // “新建”
    handleCreateClick() {
      this.isNew = true;
      this.editingData = getQuestionnaireModel();
    },

    // “导入”
    async handleUploadFile(file) {
      try {
        await this.core.uploadTemplate(file);
        await this.updateList();
        this.changeCurIndex(0);
        this.$iaMessage.default(this.t('plvIA.common.uploadSuccessfullyTips'));
      } catch (error) {
        this.$iaMessage.default(error.message);
      }
    },

    // 删除问卷弹窗
    toggleDeleteQnModal(show) {
      show ? this.$refs['delete-qn-modal'].show() : this.$refs['delete-qn-modal'].hide();
    },

    // 终止问卷弹窗
    toggleEndQnModal(show) {
      show ? this.$refs['end-qn-modal'].show() : this.$refs['end-qn-modal'].hide();
    },

    // 丢弃操作弹窗（编辑时切换到其它问卷）
    toggleDiscardModal(show) {
      show ? this.$refs['discard-modal'].show() : this.$refs['discard-modal'].hide();
    },

    // 确认丢弃操作
    handleEnsureDiscard() {
      this.toggleDiscardModal(false);
      if (this.changeIndex > -1) {
        this.changeCurIndex(this.changeIndex);
        this.changeIndex = -1;
        this.editingData = null;
        this.isNew = false;
      }
    },

    // 取消丢弃操作
    handleCancelDiscard() {
      this.toggleDiscardModal(false);
      this.changeIndex = -1;
    },

    // 进入“编辑”界面
    handleEditClick() {
      this.editingData = cloneJSON(this.list[this.curIndex]);
    },

    // 保存问卷内容
    async saveQuestionnaire(data, isRepublish = false) {
      try {
        await this.core.save(data);
      } catch (error) {
        this.$iaMessage.default(error.message);
        return;
      }
      if (!isRepublish) {
        this.$iaMessage.default(this.t('plvIA.common.saveSuccessfullyTips'));
      }

      await this.updateList();
      this.editingData = null;
      await this.changeCurIndex(isRepublish || this.isNew ? 0 : this.curIndex);
      this.isNew = false;
    },

    // 确认删除问卷
    deleteQuestionnaire: debounce(async function() {
      if (this.isDeleting) return;
      this.isDeleting = true;
      try {
        const delId = this.list[this.curIndex].questionnaireId;
        await this.core.delete(delId);
        this.toggleDeleteQnModal(false);
        this.$iaMessage.default(this.t('plvIA.common.deleteSuccessfullyTips'));
        await this.updateList();
        await this.changeCurIndex(0);
      } catch (error) {
        this.$iaMessage.default(error.message);
      } finally {
        this.isDeleting = false;
      }
    }, 500),

    // 研讨会 判断是否选择人群发送
    beforeHandlePublishClick() {
      if (this.useSeminarChatApi && this.sendRoomList.length > 0) {
        const sendTarget = this.sendRoomList.filter(item => !!item.disabled === false);
        if (sendTarget.length > 1) {
          this.showSendRoomModal = true;
        } else {
          // 判断只有一个人群可选择，默认选中直接发送
          sendTarget[0].checked = true;
          this.handlePublishClick({ sendTarget });
        }
      } else {
        this.handlePublishClick();
      }
    },

    handleSendRoomModalConfirm() {
      this.showSendRoomModal = false;
      this.handlePublishClick({ sendTarget: this.sendRoomList });
    },

    // 点击“发布问卷/再次发布”按钮
    async handlePublishClick({ sendTarget } = {}) {
      if (!this.canStart) {
        this.$iaMessage.default(this.cantStartTips);
        return;
      }

      if (this.ongoingIndex > -1) {
        this.toggleEndQnModal(true);
        return;
      }

      if (this.isPublishing) return;
      this.isPublishing = true;

      const questionnaireData = this.list[this.curIndex];

      const result = await this.fetchDetail(questionnaireData.questionnaireId);
      if (!result) {
        this.isPublishing = false;
        return; // 问卷不存在
      }

      let data = cloneJSON(questionnaireData);

      // 再次发布
      if (data.statistics) {
        delete data.status;
        delete data.statistics;
        data.questionnaireId = '';
        data.questions.forEach((question) => {
          question.questionId = '';
        });
        // 隐私协议数据再次发布处理
        data.privacyEnabled = data?.privacy?.required;
        data.privacyContent = data?.privacy?.content;

        await this.saveQuestionnaire(data, true);
        data = this.list[0];
        this.curIndex = 0;
      }
      // 设置发送人群
      sendTarget = this.getSendTargetParam(sendTarget);
      try {
        await this.core.start(data, sendTarget);
        // 发布完成后的事情在 onStart 中处理
      } catch (error) {
        this.$iaMessage.default(error.message);
      } finally {
        this.isPublishing = false;
      }
    },

    // 点击“发送结果”
    async handleResultClick() {
      if (this.isSendingResult) return;
      this.isSendingResult = true;
      const questionnaireId = this.list[this.curIndex].questionnaireId;
      try {
        await this.core.sendResult(questionnaireId);
        this.$iaMessage.default(this.t('plvIA.common.sentSuccessfullyTips'));
      } catch (error) {
        this.$iaMessage.default(error.message);
      } finally {
        this.isSendingResult = false;
        this.resultSentList.push(questionnaireId);
        this.recordSentList(this.resultSentList);
      }
    },

    // 停止问卷
    async handleStopClick() {
      if (this.isStopping) return;
      this.isStopping = true;
      try {
        const questionnaireData = this.list[this.curIndex];
        await this.stop(questionnaireData.questionnaireId);
      } catch (error) {
        this.$iaMessage.default(error.message);
      }
    },

    // 确认“去终止”按钮
    handleProcessingModalEnsure() {
      this.changeCurIndex(this.ongoingIndex);
      this.toggleEndQnModal(false);
      // 助教页会出现问题，留后面再做体验优化
      // try {
      //   const listChildNodes = this.$refs['nav-list'].children;
      //   if (listChildNodes && listChildNodes.length === this.list.length) {
      //     listChildNodes[this.ongoingIndex].scrollIntoView();
      //   }
      // } catch (error) {
      //   console.info(error);
      // }
    },

    // 更新 list 对象
    async updateList() {
      this.list = await this.core.getQuestionnaireList();
      return this.list;
    },

    // 获取问卷详情
    async fetchDetail(questionnaireId) {
      try {
        return await this.core.getQuestionnaireDetail(questionnaireId);
      } catch (error) {
        this.$iaMessage.default(error.message);
        await this.updateList();
        this.changeCurIndex(0);
        return false;
      }
    },

    // 停止问卷
    async stop(questionnaireId) {
      const result = await this.core.stop(questionnaireId);
      this.pushNum = 0;
      return result;
    },

    // 获取提交人数
    // async
    getPushNum(questionnaireId) {
      return this.core.getPushNum(questionnaireId);
    },

    // 切换问卷
    async changeCurIndex(index, isFromSendRusult = false) {
      if (index < 0) return;
      // 正在编辑
      if (this.editingData && this.changeIndex < 0) {
        if (isFromSendRusult) return;
        this.changeIndex = index;
        this.toggleDiscardModal(true);
        return;
      }

      // 问卷列表为空
      if (!this.list?.length) {
        this.curIndex = -1;
        return;
      }

      this.isLoading = true;
      this.curIndex = index;
      if (!this.list[index]) {
        console.warn('this.list[index] 不存在');
        return;
      }
      const questionnaireId = this.list[index].questionnaireId;
      const questionnaireData = await this.fetchDetail(questionnaireId);
      if (!questionnaireData) return; // 问卷信息获取失败

      if (questionnaireData.questionnaireId !== questionnaireId) { // 与当前问卷ID不一致
        this.isLoading = false;
        return;
      }

      // 问卷具体数据中插入隐私协议数据
      questionnaireData.privacy = this.list[index].privacy;

      this.$set(this.list, index, questionnaireData);
      if (questionnaireData.status === STATUS.SENDED) {
        try {
          this.pushNum = await this.getPushNum(questionnaireId);
        } catch (err) {
          this.$iaMessage.default(this.t('plvIA.common.failFetchData'));
        } // 即使请求返回错误也能让用户点击“停止问卷”
      }
      this.isLoading = false;
    },

    // 同步问卷回收份数
    updatePushNum(questionnaireId) {
      if (this.list[this.ongoingIndex].questionnaireId === questionnaireId) {
        this.pushNum++;
      }
    },

    /**
     * 根据 questionnaireId 查找问卷在 this.list 中的 index
     * @param {String} questionnaireId
     */
    findListIndex(questionnaireId) {
      return this.qnIdList.indexOf(questionnaireId);
    },

    handleDownloadStatistics(questionnaireId) {
      return this.core.downloadStatistics(questionnaireId);
    },

    handleDownloadTemplate() {
      return this.core.downloadTemplate();
    },
  }
};
</script>

<style lang="scss">
@import '../../assets/styles/main.scss';
$imgPrefix: './images/';

.plv-ia-qn {
  width: 100%;
  height: 100%;
  position: relative;
  word-break: break-all;

  textarea, input[type=text] {
    ::-webkit-input-placeholder {
      color: #9E9E9E;
    }
  }
}

.plv-ia-qn-sidebar {
  width: 200px;
  height: 100%;
  background: #EDF1F7;
  position: absolute;
  left: 0;
}

.plv-ia-qn-main {
  padding-left: 200px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.plv-ia-qn-txt-red {
  color: #FF5722;
}

.plv-ia-qn-txt-blue {
  color: #2196F3;
}

body[data-lang=en] {
  .plv-ia-qn-sidebar__bottom .plv-ia-btn-new {
    width: 160px;
  }

  .plv-ia-qn-nav-item__label--finished {
    $width: 70px;
    $scale: 0.8; // 固定按0.8倍缩小元素
    width: $width;
    // left: -($width - $width * $scale) / 2;
    left: -7px;
  }

  .plv-ia-qn-edit-question__control>label {
    width: auto;
  }
}

ul {
  margin: 0;
  padding: 0;
}
</style>
